import { signal } from '@angular/core'
import { timer } from 'rxjs'
import { Rider, Volunteer } from './person.modal'

export enum Weekday {
  MONDAY,
  TUESDAY,
  WEDNESDAY,
  THURSDAY,
  FRIDAY,
  SATURDAY,
  SUNDAY
}

export interface Class {
  id?: number
  name?: string
  // 'Term Ride' | 'Independent Ride' | 'Group Ride'
  type?: string
  venue?: string
  date?: Date | string
  weekday?: Weekday
  time?: {
    start?: Date | string
    end?: Date | string
  }

  shortTimeStart?(): string
  shortTimeEnd?(): string
  getDuration?(): number
  getWeekday?(): Weekday
  getMeridiem?(): 'AM' | 'PM'
}

export interface Equipment {
  id?: number
  name?: string
}

export interface Horse {
  id?: number
  name?: string
}

type AssessmentGrade =
  | '1'
  | '2'
  | '3'
  | '4'
  | '5'
  | '1+'
  | '2+'
  | '3+'
  | '4+'
  | '5+'
  | null
export interface Assessment {
  id?: number
  communication?: AssessmentGrade
  confident?: AssessmentGrade
  enjoyment?: AssessmentGrade
  horsemanship?: AssessmentGrade
  physicalChange?: AssessmentGrade
  remark?: string
  status?: string
  assessor?: {
    id?: number
    name?: { enName?: string; cnName?: string }
  } | null
  confirmer?: {
    id?: number
    name?: { enName?: string; cnName?: string }
  } | null
}

export interface Pairing {
  id?: number
  rider?: Rider
  leader?: Volunteer
  sideHelper?: Volunteer
  equipment?: Equipment
  horse?: Horse
}

export abstract class AbstractClass implements Class {
  id?: number
  name?: string
  // 'Term Ride' | 'Independent Ride' | 'Group Ride'
  type?: string
  venue?: string
  date?: Date | string
  weekday?: Weekday
  time?: {
    start?: Date | string
    end?: Date | string
  }

  get dateStr() {
    return typeof this.date === 'string'
      ? this.date
      : this.date?.toLocaleDateString()
  }

  get timeStartDate() {
    if (!this.time?.start) {
      return null
    }

    return new Date(`${this.dateStr} ${this.time.start}`)
  }

  get timeEndDate() {
    if (!this.time?.end) {
      return null
    }
    return new Date(`${this.dateStr} ${this.time.end}`)
  }

  shortTimeStart() {
    if (!this.timeStartDate) {
      return ''
    }
    return this.timeStartDate.toTimeString().substring(0, 5)
  }

  shortTimeEnd() {
    if (!this.timeEndDate) {
      return ''
    }
    return this.timeEndDate.toTimeString().substring(0, 5)
  }

  getDuration(): number {
    if (!this.timeStartDate || !this.timeEndDate) {
      return 0
    }
    let timeMs = this.timeEndDate.getTime() - this.timeStartDate.getTime()
    return Math.floor(timeMs / (1000 * 60))
  }

  getWeekday(): Weekday {
    if (this.weekday) {
      return this.weekday
    }
    if (!this.date) {
      throw new Error(`Property 'date' is undefined.`)
    }
    return new Date(this.date).getDay()
  }

  getMeridiem(): 'AM' | 'PM' {
    if (!this.timeStartDate) {
      throw new Error(`Property 'timeStartDate' is undefined.`)
    }
    return this.timeStartDate.getHours() >= 12 ? 'PM' : 'AM'
  }
}

export abstract class AbstractUpcomingClass extends AbstractClass {
  countDownSeconds = signal(0)

  constructor(clazz: Class) {
    super()
    Object.assign(this, clazz)
  }

  abstract startTimer(): void
}

export class ClassImpl extends AbstractClass {
  constructor(clazz: Class) {
    super()
    Object.assign(this, clazz)
  }
}

export class UpcomingClassImpl extends AbstractUpcomingClass {
  static fromJSON(json: string) {
    let clazz = JSON.parse(json)
    return new UpcomingClassImpl(clazz)
  }

  startTimer() {
    this.countDown()
    timer(60000).subscribe(() => this.startTimer())
  }

  countDown() {
    if (!this.timeStartDate) {
      return
    }
    let now = Date.now()
    let future = this.timeStartDate.getTime()
    this.countDownSeconds.set(Math.floor((future - now) / 1000))
  }
}

export interface Venue {
  id?: number
  name?: string
  timeslots: {
    weekday: Weekday
    slots: Timeslot[]
  }[]
}

export interface Timeslot {
  id?: number
  name?: string
  label?: string
  weekday?: Weekday
  start?: string
  end?: string
}
